'use client';

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import AOS from 'aos';
import { ArrowUpRight } from 'lucide-react';
import 'aos/dist/aos.css';

export default function Projects() {
  const { isRTL, currentLanguage } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectsTitle, setProjectsTitle] = useState('');
  
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const res = await fetch('/api/footer');
        if (!res.ok) {
          throw new Error('Failed to fetch footer data');
        }
        const data = await res.json();
        setProjectsTitle(data.footerText.projectSection?.title?.[currentLanguage] || '');
      } catch (err) {
        console.error('Error fetching footer data:', err);
        setError('Failed to load section title');
      }
    };

    fetchFooterData();
  }, [currentLanguage]);

  useEffect(() => {
    // Fetch projects data when component mounts
    async function fetchProjects() {
      try {
        const response = await fetch('/api/projects');
        
        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const data = await response.json();
        setProjects(data.projects);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects data');
        setLoading(false);
      }
    }
    
    fetchProjects();
  }, []);
  
  const nextProject = () => {
    setCurrentProjectIndex((prevIndex) => 
      prevIndex === projects.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevProject = () => {
    setCurrentProjectIndex((prevIndex) => 
      prevIndex === 0 ? projects.length - 1 : prevIndex - 1
    );
  };

  if (loading) {
    return (
      <section className="bg-white w-full">
        <div className="w-full px-6 py-16">
          <h2 className="text-2xl font-bold text-center text-black mb-12">
            {projectsTitle}
          </h2>
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </section>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <section className="bg-white w-full">
        <div className="w-full px-6 py-16">
          <h2 className="text-2xl font-bold text-center text-black mb-12">
            {projectsTitle}
          </h2>
          <div className="text-center text-red-500 py-10">
            {error}
          </div>
        </div>
      </section>
    );
  }

  // Show empty state if no projects
  if (!projects || projects.length === 0) {
    return (
      <section className="bg-white w-full">
        <div className="w-full bg-gradient-to-b from-blue-50 to-blue-200 px-6 py-16">
          <h2 className={`text-2xl font-bold text-center text-black mb-12 ${isRTL ? 'text-right' : ''}`}>
            {projectsTitle}
          </h2>
          <div className="text-center text-gray-500 py-10">
            {texts.projects.noProjects || 'No projects available at the moment.'}
          </div>
        </div>
      </section>
    );
  }

  const currentProject = projects[currentProjectIndex];

  return (
    // Projects Section
    <section id="projects-section" className="bg-gradient-to-b from-blue-200 to-blue-300 py-[5vh] w-full">
      <div className="w-full px-[4%] sm:px-[5%] pt-[8vh]">
        <h2 
          className={`text-[2rem] sm:text-[2.5rem] md:text-[3rem] font-bold text-left text-black mb-[2rem] ${isRTL ? 'text-right' : ''}`}
          data-aos="fade-up"
        >
          {projectsTitle}
        </h2>
        <div className="mt-[2rem] w-full" data-aos="fade-up" data-aos-delay="200">
          <div className="relative w-full transition-all duration-300 ease-in-out hover:shadow-2xl rounded-lg" style={{ maxHeight: "90vh", overflow: "hidden" }}>
            
          <a 
              href={currentProject.imageAlt?.["EN"] || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-6 right-6 lg:top-10 lg:right-10 z-10 transition-transform duration-300 hover:scale-110"
            >
              <div className="bg-white rounded-full w-10 h-10 lg:w-14 lg:h-14 flex items-center justify-center shadow-md">
                <ArrowUpRight
                  size={20} 
                  className="text-black" 
                />
              </div>
            </a>
            
            <img 
              src={currentProject.imageUrl}
              className="rounded-lg w-full object-cover h-[50vh] lg:h-[90vh] transition-all duration-500 ease-in-out transform hover:scale-105"
            />
            
            {/* Navigation arrows - only show if more than one project */}
            {projects.length > 1 && (
              <>
                <button 
                  onClick={prevProject}
                  className="absolute left-[1rem] top-1/2 transform -translate-y-1/2 bg-white bg-opacity-70 rounded-full p-[0.75rem] text-gray-800 hover:bg-opacity-100 hover:scale-110 transition-all duration-300 ease-in-out z-10"
                  aria-label="Previous project"
                >
                  <FaChevronLeft size={20} />
                </button>
                <button 
                  onClick={nextProject}
                  className="absolute right-[1rem] top-1/2 transform -translate-y-1/2 bg-white bg-opacity-70 rounded-full p-[0.75rem] text-gray-800 hover:bg-opacity-100 hover:scale-110 transition-all duration-300 ease-in-out z-10"
                  aria-label="Next project"
                >
                  <FaChevronRight size={20} />
                </button>
              </>
            )}
            
            {/* Project indicator dots */}
            {projects.length > 1 && (
              <div className="absolute bottom-[1rem] lg:bottom-[5rem] left-0 right-0 flex justify-center z-10">
                {projects.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentProjectIndex(index)}
                    className={`mx-[0.25rem] w-[0.75rem] h-[0.75rem] rounded-full transition-all duration-300 ease-in-out hover:scale-125 ${
                      index === currentProjectIndex ? 'bg-white' : 'bg-gray-300 bg-opacity-70'
                    }`}
                    aria-label={`Go to project ${index + 1}`}
                  />
                ))}
              </div>
            )}
            
            {/* Project info - stack on mobile, side by side on lg */}
            <div 
              className={`absolute bottom-[1rem] lg:bottom-[2rem] w-full px-[1rem] flex flex-col lg:flex-row lg:justify-between lg:items-start gap-0 lg:gap-0 ${isRTL ? 'lg:flex-row-reverse' : ''}`}
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div 
                className="bg-transparent p-[1rem] rounded-lg shadow lg:flex-1 transition-all duration-300 ease-in-out hover:transform hover:scale-105"
                data-aos="fade-right"
                data-aos-delay="400"
              >
                <h3 className={`text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] font-bold text-white ${isRTL ? 'text-right' : ''}`}>
                  {currentLanguage === "EN" ? (
                    currentProject.title[currentLanguage].toUpperCase().replace('CHEESE', 'CHEESE\n').split('\n').map((line, i) => <div key={i}>{line}</div>)
                  ) : currentLanguage === "BA" ? (
                    currentProject.title[currentLanguage].toUpperCase().replace('BUDUĆNOST', 'BUDUĆNOST\n').split('\n').map((line, i) => <div key={i}>{line}</div>)
                  ) : currentProject.title[currentLanguage]}
                </h3>
              </div>
              <div 
                className="bg-transparent p-[1rem] rounded-lg shadow lg:flex-1 lg:max-w-2xl transition-all duration-300 ease-in-out hover:transform hover:scale-105"
                data-aos="fade-left"
                data-aos-delay="500"
              >
                <p className={`text-[1rem] sm:text-[1.25rem] lg:text-[1.25rem] text-white w-full ${
                  isRTL 
                    ? 'lg:w-1/2 text-right' // Arabic: align right, but without ml-auto so it stays on left side
                    : 'lg:w-1/2 ml-auto' // Non-RTL: align left with ml-auto to push to right side
                }`}>
                  {currentProject.description[currentLanguage]}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}